import { useEffect, useRef, useState } from 'react'
import { Empty, PageHead, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Live telemetry console (part 11).
 *
 * This page uses the browser's native WebSocket, not a client library. The access token is kept
 * in memory by lib/api.ts and is not exported, so the page first exchanges the HttpOnly refresh
 * cookie for a short-lived access token and puts it in the upgrade URL. The server also accepts
 * Authorization: Bearer, but the browser WebSocket constructor cannot set headers.
 *
 * The socket is not a durable feed. It carries live summaries; the REST API remains the source of
 * truth for anything the operator needs to act on. Reconnecting re-subscribes and does not replay.
 */

type Status = 'idle' | 'connecting' | 'open' | 'closed' | 'error'

const CHANNELS = [
  { id: 'telemetry', label: 'เทเลเมทรี', hint: 'ความหน่วง คิวงาน และอัตราการประมวลผล' },
  { id: 'findings', label: 'ข้อสังเกต', hint: 'ข้อสังเกตที่ถูกสร้างใหม่' },
  { id: 'cases', label: 'เคส', hint: 'การเปลี่ยนสถานะของเคส' },
  { id: 'jobs', label: 'งานเบื้องหลัง', hint: 'ความคืบหน้าและงานที่ล้มเหลว' },
  { id: 'whistle', label: 'แจ้งเบาะแส', hint: 'รายงานนิรนามที่เข้าระบบ' },
] as const

const CHANNEL_LABEL: Record<string, string> = Object.fromEntries(CHANNELS.map((c) => [c.id, c.label]))

const STATUS_META: Record<Status, { label: string; tone: string }> = {
  idle: { label: 'ยังไม่เชื่อมต่อ', tone: 'var(--color-ivory-dim)' },
  connecting: { label: 'กำลังเชื่อมต่อ', tone: 'var(--color-amber)' },
  open: { label: 'เชื่อมต่อแล้ว', tone: 'var(--color-emerald-soft)' },
  closed: { label: 'ปิดการเชื่อมต่อ', tone: 'var(--color-ivory-muted)' },
  error: { label: 'เชื่อมต่อไม่ได้', tone: 'var(--color-crimson)' },
}

interface ServerFrame {
  type?: string
  channels?: string[]
  subscribed?: string[]
  channel?: string
  kind?: string
  payload?: unknown
  message?: string
  code?: string
  ts?: number
  server_ts?: number
  heartbeat_seconds?: number
  max_frame_bytes?: number
  connection_id?: string
}

interface FrameRow {
  id: number
  at: string
  direction: 'รับ' | 'ส่ง' | 'ระบบ'
  type: string
  channel?: string
  summary: string
  detail?: string
}

interface ReadyMeta {
  connectionId: string
  channels: string[]
  heartbeat: number
  maxFrame: number
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function text(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

function clip(value: string, max = 180): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value
}

function summarizeEvent(frame: ServerFrame): string {
  const p = record(frame.payload)
  switch (frame.kind) {
    case 'case.transition': {
      const caseNo = text(p.case_no) || text(p.case_id)
      const from = text(p.from_status)
      const to = text(p.to_status)
      return clip([caseNo, from || to ? `${from || '?'} → ${to || '?'}` : ''].filter(Boolean).join(' · '))
    }
    case 'finding.created': {
      const title = text(p.title) || text(p.finding_id)
      const severity = text(p.severity)
      return clip([title, severity ? `ระดับ ${severity}` : '', text(p.rule_code) ? `กฎ ${text(p.rule_code)}` : ''].filter(Boolean).join(' · '))
    }
    case 'job.failed': {
      const job = text(p.job) || text(p.job_code) || text(p.kind)
      const err = text(p.error) || text(p.message)
      return clip([job, err].filter(Boolean).join(' · '))
    }
    case 'whistle.received': {
      const code = text(p.code) || text(p.tracking_code)
      const category = text(p.category)
      return clip([code, category ? `หมวด ${category}` : ''].filter(Boolean).join(' · '))
    }
    default: {
      const parts: string[] = []
      for (const [key, value] of Object.entries(p)) {
        if (parts.length >= 3) break
        const rendered = text(value)
        if (rendered) parts.push(`${key}=${rendered}`)
      }
      return clip(parts.join(' · ') || 'ข้อความเทเลเมทรี')
    }
  }
}

function parseServerFrame(raw: unknown): ServerFrame | null {
  if (typeof raw !== 'string') return null
  try {
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? (parsed as ServerFrame) : null
  } catch {
    return null
  }
}

/** Detach handlers before an intentional close so a stale socket cannot overwrite new state. */
function stopSocket(socket: WebSocket | null, reason: string) {
  if (!socket) return
  socket.onopen = null
  socket.onmessage = null
  socket.onerror = null
  socket.onclose = null
  if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) {
    socket.close(1000, reason)
  }
}

async function fetchAccessToken(): Promise<string> {
  // The refresh endpoint is the only POST on this page. It is an authentication exchange rather
  // than a state-changing business write; a fresh Idempotency-Key is still supplied so the
  // request obeys the same header rule as every other POST in the console.
  const key = globalThis.crypto?.randomUUID?.() ?? `ws-${Date.now()}`
  const res = await fetch('/v1/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key },
    body: '{}',
  })
  if (!res.ok) throw new Error('ต่ออายุเซสชันไม่สำเร็จ กรุณาเข้าสู่ระบบใหม่')
  const body: unknown = await res.json()
  const data = record(record(body).data)
  const token = typeof data.access_token === 'string' ? data.access_token : ''
  if (!token) throw new Error('ไม่ได้รับโทเคนสำหรับเชื่อมต่อ')
  return token
}

export function WebSockets() {
  const [status, setStatus] = useState<Status>('idle')
  const [selected, setSelected] = useState<string[]>(['cases', 'findings'])
  const [active, setActive] = useState<string[]>([])
  const [frames, setFrames] = useState<FrameRow[]>([])
  const [meta, setMeta] = useState<ReadyMeta | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [sent, setSent] = useState(0)
  const [received, setReceived] = useState(0)
  const [latency, setLatency] = useState<number | null>(null)

  const socketRef = useRef<WebSocket | null>(null)
  const sequence = useRef(0)
  const pingSentAt = useRef<number | null>(null)
  const attempt = useRef(0)

  const pushFrame = (row: Omit<FrameRow, 'id' | 'at'>) => {
    sequence.current += 1
    setFrames((previous) => [{ id: sequence.current, at: new Date().toISOString(), ...row }, ...previous].slice(0, 100))
  }

  const disconnect = () => {
    attempt.current += 1
    const socket = socketRef.current
    socketRef.current = null
    stopSocket(socket, 'ผู้ใช้ปิดการเชื่อมต่อ')
    setStatus('closed')
    setActive([])
    setError(null)
    setNotice('ปิดการเชื่อมต่อแล้ว')
  }

  useEffect(() => {
    return () => {
      attempt.current += 1
      const socket = socketRef.current
      socketRef.current = null
      stopSocket(socket, 'ออกจากหน้าเทเลเมทรี')
    }
  }, [])

  const connect = async () => {
    const myAttempt = attempt.current + 1
    attempt.current = myAttempt
    const previous = socketRef.current
    socketRef.current = null
    stopSocket(previous, 'เชื่อมต่อใหม่')

    setStatus('connecting')
    setError(null)
    setNotice(null)
    setMeta(null)
    setActive([])
    setLatency(null)
    setSent(0)
    setReceived(0)
    setFrames([])
    pingSentAt.current = null
    const initialChannels = [...selected]

    let token: string
    try {
      token = await fetchAccessToken()
    } catch (err) {
      if (attempt.current !== myAttempt) return
      setStatus('error')
      setError(errorText(err, 'ขอโทเคนสำหรับเชื่อมต่อไม่สำเร็จ'))
      return
    }
    if (attempt.current !== myAttempt) return

    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const url = `${scheme}://${window.location.host}/v1/ws?token=${encodeURIComponent(token)}`
    let socket: WebSocket
    try {
      socket = new WebSocket(url)
    } catch (err) {
      if (attempt.current !== myAttempt) return
      setStatus('error')
      setError(errorText(err, 'เปิด WebSocket ไม่สำเร็จ'))
      return
    }
    socketRef.current = socket

    socket.onopen = () => {
      if (socketRef.current !== socket) return
      setStatus('open')
      pushFrame({ direction: 'ระบบ', type: 'open', summary: 'เปิดการเชื่อมต่อแล้ว' })
      if (initialChannels.length > 0) {
        socket.send(JSON.stringify({ type: 'subscribe', channels: initialChannels }))
        setSent((value) => value + 1)
      }
    }

    socket.onmessage = (event) => {
      if (socketRef.current !== socket) return
      const frame = parseServerFrame(event.data)
      setReceived((value) => value + 1)
      if (!frame || !frame.type) {
        pushFrame({ direction: 'รับ', type: 'unknown', summary: 'ได้รับข้อความที่ไม่ใช่ JSON ที่รู้จัก' })
        return
      }
      switch (frame.type) {
        case 'ready': {
          setMeta({
            connectionId: frame.connection_id ?? '',
            channels: frame.channels ?? [],
            heartbeat: frame.heartbeat_seconds ?? 0,
            maxFrame: frame.max_frame_bytes ?? 0,
          })
          setActive(frame.subscribed ?? [])
          pushFrame({
            direction: 'ระบบ',
            type: 'ready',
            summary: `พร้อมรับข้อมูล · ชีพจร ${frame.heartbeat_seconds ?? 0} วินาที · กรอบสูงสุด ${frame.max_frame_bytes ?? 0} ไบต์`,
          })
          break
        }
        case 'subscribed':
        case 'unsubscribed': {
          const channels = frame.channels ?? []
          setActive(channels)
          setNotice(
            channels.length > 0
              ? `รับข้อมูลจาก ${channels.map((c) => CHANNEL_LABEL[c] ?? c).join(', ')}`
              : 'ยกเลิกทุกช่องแล้ว — เชื่อมต่ออยู่แต่จะไม่ได้รับเหตุการณ์',
          )
          pushFrame({
            direction: 'ระบบ',
            type: frame.type,
            summary: channels.length > 0 ? channels.map((c) => CHANNEL_LABEL[c] ?? c).join(', ') : 'ไม่มีช่องที่รับอยู่',
          })
          break
        }
        case 'event': {
          pushFrame({
            direction: 'รับ',
            type: frame.kind ?? 'event',
            channel: frame.channel,
            summary: summarizeEvent(frame),
            detail: frame.payload === undefined ? undefined : JSON.stringify(frame.payload, null, 2),
          })
          break
        }
        case 'pong': {
          if (typeof frame.ts === 'number' && pingSentAt.current === frame.ts) {
            setLatency(Math.max(0, Date.now() - frame.ts))
            pingSentAt.current = null
          }
          pushFrame({ direction: 'รับ', type: 'pong', summary: 'ได้รับการตอบกลับ ping' })
          break
        }
        case 'error': {
          setError(frame.message ?? 'เซิร์ฟเวอร์ปฏิเสธคำสั่ง')
          pushFrame({ direction: 'รับ', type: 'error', summary: frame.message ?? frame.code ?? 'ข้อผิดพลาด' })
          break
        }
        default: {
          pushFrame({ direction: 'รับ', type: frame.type, summary: clip(JSON.stringify(frame)) })
        }
      }
    }

    socket.onerror = () => {
      if (socketRef.current !== socket) return
      setStatus('error')
      setError('การเชื่อมต่อ WebSocket ผิดพลาด — ตรวจสอบว่าเส้นทาง /v1/ws ถูก mount แล้ว')
    }

    socket.onclose = (event) => {
      if (socketRef.current !== socket) return
      socketRef.current = null
      setStatus('closed')
      setActive([])
      setNotice(`ปิดการเชื่อมต่อ (รหัส ${event.code}${event.reason ? ` · ${event.reason}` : ''})`)
    }
  }

  const sendPing = () => {
    const socket = socketRef.current
    if (!socket || socket.readyState !== WebSocket.OPEN) return
    const ts = Date.now()
    pingSentAt.current = ts
    socket.send(JSON.stringify({ type: 'ping', ts }))
    setSent((value) => value + 1)
    pushFrame({ direction: 'ส่ง', type: 'ping', summary: 'วัดความหน่วงรอบขาไป-กลับ' })
  }

  const toggleChannel = (id: string) => {
    const next = selected.includes(id) ? selected.filter((c) => c !== id) : [...selected, id]
    setSelected(next)
    const socket = socketRef.current
    if (!socket || socket.readyState !== WebSocket.OPEN) return
    const action = next.includes(id) ? 'subscribe' : 'unsubscribe'
    socket.send(JSON.stringify({ type: action, channels: [id] }))
    setSent((value) => value + 1)
    pushFrame({
      direction: 'ส่ง',
      type: action,
      channel: id,
      summary: `${action === 'subscribe' ? 'ติดตาม' : 'เลิกติดตาม'} ${CHANNEL_LABEL[id] ?? id}`,
    })
  }

  const statusMeta = STATUS_META[status]
  const connected = status === 'open'
  const readyChannels = meta?.channels.length ? meta.channels : CHANNELS.map((c) => c.id)

  return (
    <>
      <PageHead
        title="เทเลเมทรีสดผ่าน WebSocket"
        sub="ช่องข้อมูลแบบทันทีจากฮับในโปรเซส — ใช้ WebSocket ของเบราว์เซอร์โดยตรง ไม่มีไลบรารีเพิ่มเติม"
        actions={<span className="chip">{connected ? `${active.length} ช่องที่รับอยู่` : `${selected.length} ช่องที่เลือก`}</span>}
      />

      {error ? (
        <div className="card card-pad" style={{ marginBottom: 14, borderColor: 'var(--color-crimson)' }}>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <span className="sev sev-critical"><i className="sev-dot" />ผิดพลาด</span>
            <span style={{ fontSize: 12.5 }}>{error}</span>
          </div>
        </div>
      ) : null}

      {notice ? (
        <div className="card card-pad" style={{ marginBottom: 14, borderColor: 'var(--color-hairline)' }}>
          <span style={{ fontSize: 12.5, color: 'var(--color-ivory-muted)' }}>{notice}</span>
        </div>
      ) : null}

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="card-label">การเชื่อมต่อ</div>
            <div className="row" style={{ gap: 10, marginTop: 6 }}>
              <span className="row" style={{ gap: 8 }}>
                <span className="sev-dot" style={{ background: statusMeta.tone }} />
                <strong style={{ fontSize: 13.5 }}>{statusMeta.label}</strong>
              </span>
              {latency !== null ? <span className="chip">ความหน่วง {latency} มิลลิวินาที</span> : null}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-ivory-dim)', marginTop: 8, maxWidth: 620 }}>
              เปิดช่องที่ต้องการแล้วกดเชื่อมต่อ ระบบจะขอโทเคนอายุสั้นจากคุกกี้รีเฟรช แล้วส่งคำสั่ง subscribe
              ผ่านข้อความ JSON หลังแฮนด์เชก HTTP 101
            </div>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => void connect()} disabled={status === 'connecting' || connected}>
              เชื่อมต่อ
            </button>
            <button className="btn" onClick={disconnect} disabled={!connected && status !== 'connecting'}>
              ตัดการเชื่อมต่อ
            </button>
            <button className="btn btn-ghost" onClick={sendPing} disabled={!connected}>
              ทดสอบ ping
            </button>
          </div>
        </div>

        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 18 }}>
          {CHANNELS.map((channel) => {
            const checked = selected.includes(channel.id)
            return (
              <label
                key={channel.id}
                className={`chip ${checked ? 'chip-on' : ''}`}
                title={channel.hint}
                style={{ cursor: 'pointer', gap: 7 }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleChannel(channel.id)}
                  style={{ accentColor: 'var(--color-gold)', margin: 0 }}
                />
                {channel.label}
              </label>
            )
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="ข้อความที่รับ" value={received} unit="รายการ" />
        <Stat label="ข้อความที่ส่ง" value={sent} unit="รายการ" />
        <Stat label="ความหน่วงล่าสุด" value={latency === null ? '—' : latency} unit={latency === null ? undefined : 'มิลลิวินาที'} />
        <Stat label="ช่องที่เซิร์ฟเวอร์ยืนยัน" value={active.length} unit="ช่อง" foot={active.map((c) => CHANNEL_LABEL[c] ?? c).join(', ') || 'ยังไม่ได้ subscribe'} />
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div className="card-label">โปรโตคอล</div>
            <div style={{ fontSize: 12.5, color: 'var(--color-ivory-muted)', marginTop: 6 }}>
              RFC 6455 · ไคลเอนต์มาสก์ทุกกรอบ เซิร์ฟเวอร์ไม่มาสก์ · ชีพจร {meta?.heartbeat ?? 20} วินาที ·
              กรอบสูงสุด {(meta?.maxFrame ?? 1048576).toLocaleString('th-TH')} ไบต์
            </div>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {readyChannels.map((channel) => (
              <span key={channel} className="chip">{CHANNEL_LABEL[channel] ?? channel}</span>
            ))}
          </div>
        </div>
        {meta?.connectionId ? (
          <div className="mono" style={{ marginTop: 10, color: 'var(--color-ivory-dim)' }}>
            connection_id: {meta.connectionId}
          </div>
        ) : null}
      </div>

      {frames.length === 0 ? (
        <Empty
          title="ยังไม่มีข้อความ"
          body="เลือกช่องแล้วกดเชื่อมต่อ เมื่อมีเหตุการณ์จากฮับ ข้อความจะปรากฏที่นี่ทันที"
        />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 168 }}>เวลา</th>
                <th style={{ width: 78 }}>ทิศทาง</th>
                <th style={{ width: 170 }}>ชนิด</th>
                <th style={{ width: 120 }}>ช่อง</th>
                <th>สรุป</th>
              </tr>
            </thead>
            <tbody>
              {frames.map((row) => (
                <tr key={row.id}>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(row.at)}</td>
                  <td>
                    <span
                      className="chip"
                      style={{
                        color:
                          row.direction === 'รับ'
                            ? 'var(--color-emerald-soft)'
                            : row.direction === 'ส่ง'
                              ? 'var(--color-gold)'
                              : 'var(--color-ivory-muted)',
                      }}
                    >
                      {row.direction}
                    </span>
                  </td>
                  <td className="mono">{row.type}</td>
                  <td>{row.channel ? CHANNEL_LABEL[row.channel] ?? row.channel : '—'}</td>
                  <td style={{ fontSize: 12.5 }}>
                    {row.summary}
                    {row.detail ? (
                      <details style={{ marginTop: 6 }}>
                        <summary style={{ cursor: 'pointer', color: 'var(--color-ivory-dim)', fontSize: 11.5 }}>ดู JSON</summary>
                        <pre className="mono" style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', color: 'var(--color-ivory-muted)', maxHeight: 180, overflow: 'auto' }}>
                          {row.detail}
                        </pre>
                      </details>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
